/**
 * "Replicar para as seções": copia as colunas de um bloco já mapeado para as
 * outras seções que o template declara. A regra da cópia está em
 * `replicate.ts`; aqui só a escolha das seções e do agrupamento de cada uma.
 * Além das seções da própria aba, entra o mesmo bloco nas outras abas — a
 * planilha com uma aba por agrupamento.
 *
 * As cópias entram na lista em tela, como o "Adicionar mapeamento" — só vão
 * para o servidor no "Salvar mapeamentos".
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import {
  BudgetGroupTemplateEntry,
  FieldMapping,
  SheetInspection,
  TemplateSection,
} from "../../../../../types/clientSpreadsheet.type";
import {
  blockOf,
  defaultGroupFor,
  isSectionMapped,
  replicateBlock,
  sameBlockInOtherSheets,
  sectionContainingRow,
  sectionTitle,
} from "./replicate";

type Props = {
  open: boolean;
  mappings: FieldMapping[];
  /** O mapeamento clicado; o bloco é o dele. */
  sourceIndex: number | null;
  sheets: SheetInspection[];
  templateEntries?: BudgetGroupTemplateEntry[];
  /** Caminho do campo -> nome dele, para o rótulo das cópias. */
  fieldLabels?: Record<string, string>;
  onConfirm: (created: FieldMapping[]) => void;
  onClose: () => void;
};

type Choice = { checked: boolean; group: string };

/** Aba + linha: a linha sozinha se repete entre as abas. */
const keyOf = (sheetName: string, section: TemplateSection) =>
  `${sheetName}|${section.startRow}`;

export default function ReplicateDialog({
  open,
  mappings,
  sourceIndex,
  sheets,
  templateEntries,
  fieldLabels,
  onConfirm,
  onClose,
}: Props) {
  const block = useMemo(
    () => (sourceIndex === null ? null : blockOf(mappings, sourceIndex)),
    [mappings, sourceIndex]
  );

  const sourceSheet = useMemo(
    () => sheets.find((sheet) => sheet.name === block?.sheetName),
    [sheets, block]
  );
  const sections: TemplateSection[] = useMemo(
    () => sourceSheet?.sections ?? [],
    [sourceSheet]
  );

  const sourceSection = block
    ? sectionContainingRow(sections, block.startRow)
    : undefined;

  const targets = useMemo(() => {
    if (!block) return [];
    const sameSheet = sections
      .filter((section) => section !== sourceSection)
      .map((section) => ({ sheetName: block.sheetName, section }));
    const otherSheets = sameBlockInOtherSheets(
      sheets,
      block.sheetName,
      sourceSection
    );

    return [...sameSheet, ...otherSheets].map(({ sheetName, section }) => ({
      key: keyOf(sheetName, section),
      sheetName,
      otherSheet: sheetName !== block.sheetName,
      section,
      mapped: isSectionMapped(mappings, sheetName, section),
    }));
  }, [sheets, sections, sourceSection, block, mappings]);

  const [choices, setChoices] = useState<Record<string, Choice>>({});

  // Recomeça a cada abertura: seção livre vem marcada, a já mapeada não.
  useEffect(() => {
    if (!open) return;
    const initial: Record<string, Choice> = {};
    targets.forEach(({ key, sheetName, otherSheet, section, mapped }) => {
      initial[key] = {
        checked: !mapped,
        group: defaultGroupFor(
          section,
          templateEntries,
          otherSheet ? sheetName : undefined
        ),
      };
    });
    setChoices(initial);
    // Só na abertura: recalcular a cada render apagaria o que foi digitado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceIndex]);

  const selected = targets.filter(
    ({ key, mapped }) => !mapped && choices[key]?.checked
  );
  const missingGroup = selected.some(({ key }) => !choices[key]?.group.trim());

  function update(key: string, patch: Partial<Choice>) {
    setChoices((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  }

  function setAll(checked: boolean) {
    setChoices((prev) => {
      const next = { ...prev };
      targets.forEach(({ key, mapped }) => {
        if (!mapped) next[key] = { ...next[key], checked };
      });
      return next;
    });
  }

  function handleConfirm() {
    if (!block) return;
    const created = replicateBlock(
      block.indexes.map((i) => mappings[i]),
      sourceSection,
      selected.map(({ key, sheetName, otherSheet, section }) => ({
        section,
        group: choices[key].group,
        sheet: otherSheet
          ? sheets.find((sheet) => sheet.name === sheetName)
          : undefined,
      })),
      fieldLabels,
      sourceSheet
    );
    onConfirm(created);
  }

  const columnCount = block?.indexes.length ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Replicar para outras secoes</DialogTitle>
      <DialogContent>
        {!block ? (
          <Alert severity="info">
            So colunas de servico recortadas por agrupamento podem ser
            replicadas.
          </Alert>
        ) : !targets.length ? (
          <Alert severity="info">
            O template nao declara outras secoes na aba "{block.sheetName}", nem
            o mesmo bloco em outra aba.
          </Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2">
              As {columnCount} coluna(s) do bloco <strong>{block.group}</strong>{" "}
              (linha {block.startRow}) serao copiadas para cada secao marcada,
              com a linha inicial da secao e o agrupamento informado ao lado.
            </Typography>

            <Box display="flex" gap={1}>
              <Button
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setAll(true)}
              >
                Marcar todas
              </Button>
              <Button
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setAll(false)}
              >
                Desmarcar todas
              </Button>
            </Box>

            {targets.map(({ key, sheetName, otherSheet, section, mapped }) => {
              const choice = choices[key];
              const checked = !mapped && !!choice?.checked;
              // Em outra aba o título da seção se repete ("Tipo de
              // EQUIPAMENTOS"); o que distingue é a aba.
              const title = otherSheet ? sheetName : sectionTitle(section.name);
              return (
                <Box
                  key={key}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={mapped}
                    onChange={(e) => update(key, { checked: e.target.checked })}
                    inputProps={{ "aria-label": `Replicar em ${title}` }}
                  />
                  <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={section.name}>
                      {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {otherSheet ? `Aba "${sheetName}", linhas` : "Linhas"}{" "}
                      {section.startRow} a {section.endRow}
                    </Typography>
                  </Box>
                  {mapped ? (
                    <Chip size="small" label="Ja mapeada" variant="outlined" />
                  ) : (
                    <TextField
                      size="small"
                      label="Agrupamento"
                      value={choice?.group ?? ""}
                      disabled={!checked}
                      error={checked && !choice?.group.trim()}
                      onChange={(e) => update(key, { group: e.target.value })}
                      sx={{ flex: "1 1 240px" }}
                      inputProps={{ "aria-label": `Agrupamento de ${title}` }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!block || !selected.length || missingGroup}
          variant="contained"
          style={{
            background: "#1361A4",
            color: "#fff",
            textTransform: "none",
          }}
        >
          Replicar em {selected.length}{" "}
          {selected.length === 1 ? "secao" : "secoes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
